import { addDays, format, startOfDay } from "date-fns";

import type {
  DriverAvailabilityStatus,
  DriverCalendarScheduleBlockDto,
} from "../services/driverAvailabilityApi";

export type DriverSchedulingState =
  | "Available"
  | "PartiallyAvailable"
  | "Assigned"
  | "NotScheduled"
  | "Unavailable"
  | "Conflict";

export const unavailableStatuses: DriverAvailabilityStatus[] = [
  "Unavailable",
  "Sick",
  "OnLeave",
  "Other",
];

const blockPriority = (block: DriverCalendarScheduleBlockDto) => {
  if (block.isConflict) return 500;
  if (block.status === "Assigned" || block.dispatches.length > 0) return 400;
  if (block.status === "PartiallyAvailable") return 300;
  if (block.sourceType === "implicit_unavailable") return 0;
  if (unavailableStatuses.includes(block.status)) return 200;
  return 100;
};

export const getPrimaryScheduleBlock = (
  blocks: DriverCalendarScheduleBlockDto[]
): DriverCalendarScheduleBlockDto | undefined =>
  [...blocks].sort((a, b) => {
    const priorityDifference = blockPriority(b) - blockPriority(a);
    if (priorityDifference !== 0) return priorityDifference;
    return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
  })[0];

export const getSchedulingState = (
  blocks: DriverCalendarScheduleBlockDto[]
): DriverSchedulingState | undefined => {
  const primary = getPrimaryScheduleBlock(blocks);
  if (!primary) return undefined;
  if (primary.isConflict) return "Conflict";
  if (primary.status === "Assigned" || primary.dispatches.length > 0) return "Assigned";
  if (primary.status === "PartiallyAvailable") return "PartiallyAvailable";
  if (primary.sourceType === "implicit_unavailable") return "NotScheduled";
  if (unavailableStatuses.includes(primary.status)) return "Unavailable";
  return "Available";
};

export const getActionableScheduleBlocks = (
  blocks: DriverCalendarScheduleBlockDto[],
  now: Date = new Date()
) => blocks.filter((block) => new Date(block.endDateTime) > now);

export const getCalendarDayRange = (day: Date) => {
  const start = startOfDay(day);
  return { start, end: addDays(start, 1) };
};

export const getCalendarDayBlocks = (
  blocks: DriverCalendarScheduleBlockDto[],
  day: Date
) => {
  const { start, end } = getCalendarDayRange(day);
  return blocks.filter((block) =>
    new Date(block.startDateTime) < end && new Date(block.endDateTime) > start
  );
};

export const isBlockAllDayForCalendarDay = (
  block: DriverCalendarScheduleBlockDto,
  day: Date
) => {
  const { start, end } = getCalendarDayRange(day);
  return new Date(block.startDateTime) <= start && new Date(block.endDateTime) >= end;
};

export const getDayStatusBlocks = (
  blocks: DriverCalendarScheduleBlockDto[],
  day: Date,
  now: Date = new Date()
) => {
  const { start: dayStart, end: dayEnd } = getCalendarDayRange(day);
  const dayBlocks = getCalendarDayBlocks(blocks, day);

  if (now >= dayStart && now < dayEnd) {
    return getActionableScheduleBlocks(dayBlocks, now);
  }

  return dayBlocks;
};

const coversWholeDay = (
  blocks: DriverCalendarScheduleBlockDto[],
  day: Date
) => {
  const { start: dayStart, end: dayEnd } = getCalendarDayRange(day);
  const intervals = blocks
    .filter((block) =>
      block.status === "Available" && block.sourceType !== "implicit_unavailable"
    )
    .map((block) => ({
      start: Math.max(new Date(block.startDateTime).getTime(), dayStart.getTime()),
      end: Math.min(new Date(block.endDateTime).getTime(), dayEnd.getTime()),
    }))
    .filter((interval) => interval.end > interval.start)
    .sort((a, b) => a.start - b.start);

  if (intervals.length === 0 || intervals[0].start > dayStart.getTime()) return false;

  let coveredUntil = intervals[0].end;
  for (const interval of intervals.slice(1)) {
    if (interval.start > coveredUntil) return false;
    coveredUntil = Math.max(coveredUntil, interval.end);
  }

  return coveredUntil >= dayEnd.getTime();
};

export const getDaySchedulingState = (
  blocks: DriverCalendarScheduleBlockDto[],
  day: Date,
  now: Date = new Date()
): DriverSchedulingState | undefined => {
  const dayBlocks = getDayStatusBlocks(blocks, day, now);
  const state = getSchedulingState(dayBlocks);

  if (state !== "Available") return state;
  return coversWholeDay(dayBlocks, day) ? "Available" : "PartiallyAvailable";
};

const formatDayBoundary = (value: Date, dayEnd: Date) =>
  value.getTime() === dayEnd.getTime() ? "24:00" : format(value, "HH:mm");

export const getBlockTimeForDay = (
  block: DriverCalendarScheduleBlockDto,
  day: Date
) => {
  const { start: dayStart, end: dayEnd } = getCalendarDayRange(day);
  const start = new Date(Math.max(new Date(block.startDateTime).getTime(), dayStart.getTime()));
  const end = new Date(Math.min(new Date(block.endDateTime).getTime(), dayEnd.getTime()));

  if (end <= start) return undefined;
  return `${format(start, "HH:mm")}–${formatDayBoundary(end, dayEnd)}`;
};

export const getBlockPrimaryDetail = (block: DriverCalendarScheduleBlockDto) => {
  if (block.isConflict) {
    return block.conflictReason || block.routeName || block.deliveryPlanName || "Needs attention";
  }

  if (block.status === "Assigned" || block.dispatches.length > 0) {
    return block.routeName || block.deliveryPlanName || block.vehicleRegistrationNumber || "Assignment";
  }

  if (unavailableStatuses.includes(block.status)) {
    const reasonStatus = block.status === "OnLeave" ? "On Leave" : block.status;
    if (block.status !== "Unavailable") {
      return block.availabilityReason && block.availabilityReason.toLowerCase() !== reasonStatus.toLowerCase()
        ? `${reasonStatus} · ${block.availabilityReason}`
        : reasonStatus;
    }
    if (block.availabilityReason) return block.availabilityReason;
  }

  return undefined;
};

export const getBlockSecondaryDetail = (
  block: DriverCalendarScheduleBlockDto,
  now: Date = new Date()
) => {
  if (block.status === "Assigned" || block.dispatches.length > 0) {
    const dispatchText = block.dispatches.length > 0
      ? `${block.dispatches.length} ${block.dispatches.length === 1 ? "dispatch" : "dispatches"}`
      : undefined;
    const timeText = `${format(new Date(block.startDateTime), "HH:mm")}–${format(new Date(block.endDateTime), "HH:mm")}`;
    return [dispatchText, timeText].filter(Boolean).join(" · ");
  }

  if (block.status === "PartiallyAvailable") {
    return `${format(new Date(block.startDateTime), "HH:mm")}–${format(new Date(block.endDateTime), "HH:mm")}`;
  }

  if (block.status === "Available") {
    const start = new Date(block.startDateTime);
    const end = new Date(block.endDateTime);
    return start <= now && end > now
      ? `Until ${format(end, "HH:mm")}`
      : `${format(start, "HH:mm")}–${format(end, "HH:mm")}`;
  }

  return undefined;
};

export const getBlockSecondaryDetailForDay = (
  block: DriverCalendarScheduleBlockDto,
  day: Date
) => {
  const timeText = getBlockTimeForDay(block, day);
  if (!timeText) return undefined;

  if (block.status === "Assigned" || block.dispatches.length > 0) {
    const dispatchText = block.dispatches.length > 0
      ? `${block.dispatches.length} ${block.dispatches.length === 1 ? "dispatch" : "dispatches"}`
      : undefined;
    return [dispatchText, timeText].filter(Boolean).join(" · ");
  }

  if (block.status === "Available" || block.status === "PartiallyAvailable") {
    return timeText;
  }

  return undefined;
};
